import React, { useState, useEffect } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import tw from 'twin.macro';

import {
  Button,
  Col,
  Container,
  Flex,
  Form,
  Grid,
  Overlay,
  Section,
  AnchorText,
  SanityBlockContent,
} from '../../common';
import { getIcon } from '../../../utils';

const StyledHeroHeadline = styled(motion.h1)`
  color: ${(props) => props.$headline};
  ${tw`mb-10`};
`;

const StyledHeroSubheadline = styled(motion.div)`
  color: ${(props) => props.$subheadline};
`;

const StyledTestimonial = styled(motion.div)`
  background-color: ${(props) => props.$bgColor};
  color: ${(props) => props.$color};
  svg {
    color: ${(props) => props.$svgColor};
  }
`;

const StyledH3 = styled(motion.h3)`
  color: ${(props) => props.$color} !important;
  :hover {
    color: ${(props) => props.$colorHover} !important;
  }
`;

const HeroTestimonial = ({ sanity, page }) => {
  const {
    _rawHeroSubheadline,
    heroOverlayType,
    heroOverlayWidth,
    heroOverlayHeight,
    heroOverlayAngleDesktop,
    heroOverlayAngleMobile,
    heroBgImg,
    heroHeadline,
    heroTestimonial,
    heroServices,
    heroAddCtaButtons,
    heroPrimaryCtaButtonLabel,
    heroPrimaryCtaButtonLink,
    heroSecondaryCtaButtonLabel,
    heroSecondaryCtaButtonLink,
  } = page;
  const { colors, logos } = sanity;

  const variants = {
    hidden: { opacity: 0, scale: 0.8, x: -100 },
    visible: { opacity: 1, scale: 1, x: 0 },
  };

  const controls = useAnimation();
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
    if (!inView) {
      controls.start('hidden');
    }
  }, [controls, inView]);

  return (
    <Section
      ref={ref}
      padding="custom"
      idName="hero-testimonial"
      classes="relative flex flex-col place-content-center place-items-center pt-[10rem] lg:pt-[14rem] pb-[7rem]"
    >
      <div className="absolute inset-0 overflow-hidden">
        <GatsbyImage
          image={heroBgImg?.asset?.gatsbyImageData}
          alt={heroBgImg?.alt}
          className="h-full w-full object-cover"
        />
        <Overlay
          heroOverlayType={heroOverlayType}
          heroOverlayAngleDesktop={heroOverlayAngleDesktop}
          heroOverlayAngleMobile={heroOverlayAngleMobile}
          heroOverlayWidth={heroOverlayWidth}
          heroOverlayHeight={heroOverlayHeight}
          bgColor={colors?.hero?.overlayImage}
          bgColorHover={colors?.neutral?.dark}
          bgColorStart={colors?.neutral?.darker}
          bgColorMid={colors?.neutral?.dark}
          bgColorEnd={colors?.neutral?.default}
        />
      </div>
      <Grid classes="lg:grid-cols-5 gap-y-16 lg:gap-24 max-w-7xl mx-auto lg:mx-0 px-4 lg:px-0">
        <Col classes="order-1 lg:col-span-3 p-4 lg:p-0 justify-start relative space-y-8 mx-auto">
          <StyledHeroHeadline
            className="text-center lg:text-left"
            $headline={colors?.neutral?.lighter}
          >
            <span className="drop-shadow-headline-lg">{heroHeadline}</span>
          </StyledHeroHeadline>
          <StyledHeroSubheadline
            className="space-y-7 text-left text-lg font-medium sm:text-lg md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
            $subheadline={colors?.neutral?.lighter}
          >
            <SanityBlockContent sanity={sanity} blocks={_rawHeroSubheadline} />
          </StyledHeroSubheadline>
          {heroAddCtaButtons ? (
            <Flex classes="pt-2 gap-6 flex-col lg:flex-row">
              <Button
                sanity={sanity}
                label={heroPrimaryCtaButtonLabel}
                internalLink={heroPrimaryCtaButtonLink?.slug?.current}
              ></Button>
              <Button
                secondaryBtn={true}
                sanity={sanity}
                label={heroSecondaryCtaButtonLabel}
                internalLink={heroSecondaryCtaButtonLink?.slug?.current}
              ></Button>
            </Flex>
          ) : null}
        </Col>
        <Col classes="order-2 lg:col-span-2 space-y-12 p-4 lg:p-0">
          {/* <Flex> */}
          <div className="relative">
            <StyledTestimonial
              $bgColor={colors?.primary?.default}
              $color={colors?.primary?.lighter}
              $svgColor={colors?.accent?.lighter}
              className="z-10 overflow-hidden rounded-2xl bg-opacity-80 p-8 shadow-2xl lg:p-10"
            >
              <svg
                className="absolute left-3 top-3 h-16 w-16 opacity-50 lg:left-5 lg:top-5"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 144 144"
                aria-hidden="true"
              >
                <path
                  strokeWidth={2}
                  d="M41.485 15C17.753 31.753 1 59.208 1 89.455c0 24.664 14.891 39.09 32.109 39.09 16.287 0 28.386-13.03 28.386-28.387 0-15.356-10.703-26.524-24.663-26.524-2.792 0-6.515.465-7.446.93 2.327-15.821 17.218-34.435 32.11-43.742L41.485 15zm80.04 0c-23.268 16.753-40.02 44.208-40.02 74.455 0 24.664 14.891 39.09 32.109 39.09 15.822 0 28.386-13.03 28.386-28.387 0-15.356-11.168-26.524-25.129-26.524-2.792 0-6.049.465-6.98.93 2.327-15.821 16.753-34.435 31.644-43.742L121.525 15z"
                />
              </svg>
              <blockquote className="relative">
                <div className="text-2xl font-light italic text-zinc-100">
                  <SanityBlockContent
                    sanity={sanity}
                    blocks={heroTestimonial?._rawQuote}
                  />{' '}
                </div>
                <footer className="mt-8">
                  <Grid classes="grid-cols-12 gap-x-8 lg:gap-x-6 content-center">
                    <div className="col-span-2 h-12 w-12 rounded-full">
                      <GatsbyImage
                        image={
                          heroTestimonial?.portrait?.asset?.gatsbyImageData
                        }
                        alt={heroTestimonial?.portrait?.alt}
                        className="h-full w-full rounded-full object-cover"
                      />
                    </div>
                    <div className="col-span-10 flex flex-col justify-center">
                      <div className="text-base font-bold lg:text-lg">
                        {heroTestimonial?.author}
                      </div>
                      <div className="text-base text-white lg:text-lg">
                        {heroTestimonial?.jobTitle}
                      </div>
                      <div className="text-base text-white lg:text-lg">
                        {heroTestimonial?.company}
                      </div>
                      <div className="text-base text-white lg:text-lg">
                        {heroTestimonial?.location}
                      </div>
                      <Flex classes="text-base lg:text-lg font-medium pt-2">
                        {Array(5)
                          .fill()
                          .map((_, i) => {
                            const StarIconComponent = getIcon('Star');
                            return (
                              <StarIconComponent
                                key={i}
                                className="!text-yellow-500"
                              />
                            );
                          })}
                      </Flex>
                    </div>
                  </Grid>
                </footer>
              </blockquote>
            </StyledTestimonial>
          </div>
          {/* </Flex> */}
          <Grid classes="lg:grid-cols-2 gap-16 lg:gap-8">
            {heroServices?.map((page) => (
              <AnchorText
                sanity={sanity}
                key={page?.slug?.current}
                type="internal"
                to={page?.slug?.current}
                color={colors?.neutral?.white}
                colorHover={colors?.primary?.default}
                classes="relative w-full bg-gradient-to-r from-blue-500 via-blue-700 to-blue-800 rounded-xl shadow-2xl px-8 py-4"
              >
                <div className="absolute left-0 right-0 top-0 mx-auto -mt-6 h-12 w-12 rounded-full bg-white p-2">
                  <GatsbyImage
                    image={logos?.favicon?.asset?.gatsbyImageData}
                    alt={logos?.favicon?.alt}
                    className="h-full w-full rounded-full object-cover"
                  />
                </div>
                <StyledH3
                  $color={colors?.neutral?.white}
                  $colorHover={colors?.primary?.default}
                  className="mb-3 mt-6 text-center text-2xl font-bold"
                >
                  <span className="drop-shadow-darker">{page?.name}</span>
                </StyledH3>
              </AnchorText>
            ))}
          </Grid>
        </Col>
      </Grid>
    </Section>
  );
};

export { HeroTestimonial };
